/**
 * DIMENSION EQ round (2026-09-30, Matt: "Revit's method of showing the temporary
 * dimension, allowing the user to set their references, and then clicking EQ to make
 * them equal makes more sense to me from a flexibility standpoint … you can have many
 * distributions if you want using actual placed dimension strings … just like Revit").
 *
 * A dimension STRING with `eq: true` holds every segment equal. Revit's rule: the two
 * END references stay put; the elements behind the INTERIOR references move along the
 * string until the segments match — a wall or any path element translates, a hosted
 * door/window slides along its own wall (openingOffsetToAlign), a bare static point
 * can't move and simply sits where it is. It is a STANDING rule, not a one-shot:
 * `enforceEqDimensions` runs at the end of every outermost local transaction
 * (doc.ts#registerPostTransactHook) — drag an end wall and the window re-centers in
 * the same undo step; drag the window itself and it holds its place (Revit does the
 * same). Undo never re-runs it (undo restores the prior, already-equal state), and a
 * collaborator's edits arrive already equalized by their own session.
 *
 * What the string measures against is whatever references the user picked — finish
 * face, core face, centerline, a frame edge or a window's centerline — which is the
 * whole point: no fixed rule about wall thickness, the dimension decides.
 */
import type { DraftingRecord, ElementId, Pt2 } from '../types'
import { getView, listDrafting, maps, registerPostTransactHook, resolveElement, transact, type ModelDoc } from '../doc'
import { updateDrafting } from './drafting'
import {
  dimensionDirection, dimensionWitnessList, openingWitnessLine, resolveWitness, witnessElementId,
} from './drafting'
import { openingOffsetToAlign } from '../openingAlign'
import { translateElement } from './geometryOps'
import { setElementParam } from './elements'

/** how close segments must be to count as already equal (feet — about 1/64") */
const EQ_TOL_FT = 1 / 768

/** the string's measuring direction — its lead reference line's normal, else its ends */
function eqDirection(model: ModelDoc, rec: DraftingRecord, pts: Pt2[]): Pt2 | null {
  const dir = dimensionDirection(model, rec)
  if (dir) return dir
  const a = pts[0], b = pts[pts.length - 1]
  const len = Math.hypot(b[0] - a[0], b[1] - a[1])
  return len > 1e-9 ? [(b[0] - a[0]) / len, (b[1] - a[1]) / len] : null
}

/** a PLAN-surface record — EQ moves elements in plan, so section/elevation/detail
 *  strings (a different 2D space) never carry it */
function isPlanScoped(model: ModelDoc, rec: DraftingRecord): boolean {
  if (rec.scope.kind === 'level') return true
  const v = getView(model, rec.scope.viewId)
  return !!v && v.scope.kind === 'plan'
}

/** a dimension that can carry EQ: a linear plan string with 3+ witnesses */
export function canEqDimension(rec: DraftingRecord | null | undefined, model?: ModelDoc): boolean {
  if (!rec || rec.kind !== 'dimension' || rec.dimType || dimensionWitnessList(rec).length < 3) return false
  return model ? isPlanScoped(model, rec) : true
}

/**
 * Turn EQ on/off for one string (Revit's EQ toggle). On: the interior references move
 * right away (same undo step) and the result says what couldn't; off: nothing moves,
 * the string just shows its real values again.
 */
export function setDimensionEq(model: ModelDoc, id: string, on: boolean): EqualizeResult | null {
  const rec = listDrafting(model).find((r) => r.id === id)
  if (!rec || !canEqDimension(rec, model)) return null
  let res: EqualizeResult = { moved: [], blocked: [] }
  transact(model, () => {
    updateDrafting(model, rec.id, { eq: on ? true : null })
    if (on) res = equalizeDimension(model, { ...rec, eq: true })
  })
  return res
}

/** true when the string's segments are already equal (or it can't be measured) */
export function isDimensionEqual(model: ModelDoc, rec: DraftingRecord): boolean {
  const pts = dimensionWitnessList(rec).map((w) => resolveWitness(model, w))
  const dir = eqDirection(model, rec, pts)
  if (!dir || pts.length < 3) return true
  const t = pts.map((p) => p[0] * dir[0] + p[1] * dir[1]).sort((x, y) => x - y)
  const seg = (t[t.length - 1] - t[0]) / (t.length - 1)
  for (let i = 1; i < t.length; i++) if (Math.abs(t[i] - t[i - 1] - seg) > EQ_TOL_FT) return false
  return true
}

export interface EqualizeResult {
  /** elements that moved */
  moved: ElementId[]
  /** interior references that couldn't move, with why (pinned, an end element, a bare point, …) */
  blocked: { elementId: ElementId | null; reason: string }[]
}

/**
 * Move the interior references of one string so every segment is equal. Ends stay put
 * (the extreme witnesses along the string direction — pick order doesn't matter).
 * Several interior witnesses on ONE element (both faces of a partition) move it by
 * their average shift — the element can only move once.
 */
export function equalizeDimension(model: ModelDoc, rec: DraftingRecord): EqualizeResult {
  const out: EqualizeResult = { moved: [], blocked: [] }
  const wl = dimensionWitnessList(rec)
  if (wl.length < 3) return out
  const pts = wl.map((w) => resolveWitness(model, w))
  const dir = eqDirection(model, rec, pts)
  if (!dir) return out
  const order = wl.map((w, i) => ({ w, i, t: pts[i][0] * dir[0] + pts[i][1] * dir[1] })).sort((x, y) => x.t - y.t)
  const t0 = order[0].t, tN = order[order.length - 1].t
  const step = (tN - t0) / (order.length - 1)
  const endIds = new Set([order[0], order[order.length - 1]].map((o) => witnessElementId(o.w)).filter(Boolean))
  // group the interior shifts per element
  const byEl = new Map<ElementId, { shifts: number[]; w: typeof wl[number]; pt: Pt2; target: number }>()
  for (let k = 1; k < order.length - 1; k++) {
    const o = order[k]
    const target = t0 + step * k
    const shift = target - o.t
    const id = witnessElementId(o.w)
    if (!id) {
      if (Math.abs(shift) > EQ_TOL_FT) out.blocked.push({ elementId: null, reason: 'a reference point that is not attached to anything' })
      continue
    }
    if (endIds.has(id)) {
      if (Math.abs(shift) > EQ_TOL_FT) out.blocked.push({ elementId: id, reason: 'it also holds an end of the string' })
      continue
    }
    const g = byEl.get(id)
    if (g) g.shifts.push(shift)
    else byEl.set(id, { shifts: [shift], w: o.w, pt: pts[o.i], target })
  }
  for (const [id, g] of byEl) {
    const shift = g.shifts.reduce((s, v) => s + v, 0) / g.shifts.length
    if (Math.abs(shift) <= EQ_TOL_FT) continue
    const el = resolveElement(model, id)
    if (!el) continue
    if (el.params.pinned === true) { out.blocked.push({ elementId: id, reason: 'it is pinned' }); continue }
    if (g.w.openingRef) {
      // a door/window slides along its own wall until its picked line sits at the
      // target station
      const line = openingWitnessLine(model, g.w.openingRef)
      if (!line || !el.hostId) { out.blocked.push({ elementId: id, reason: 'it has no wall to slide along' }); continue }
      const P: Pt2 = [g.pt[0] + dir[0] * shift, g.pt[1] + dir[1] * shift]
      const res = openingOffsetToAlign(resolveElement(model, el.hostId), el, line.rel,
        { a: P, b: [P[0] + line.u[0], P[1] + line.u[1]] })
      if ('error' in res) { out.blocked.push({ elementId: id, reason: res.error }); continue }
      if (setElementParam(model, id, 'hostOffsetFt', res.hostOffsetFt)) out.moved.push(id)
      continue
    }
    if (el.hostId && !(el.params.path as { nodes?: unknown[] } | undefined)?.nodes?.length) {
      out.blocked.push({ elementId: id, reason: 'it rides its host' })
      continue
    }
    if (translateElement(model, id, [dir[0] * shift, dir[1] * shift])) out.moved.push(id)
    else out.blocked.push({ elementId: id, reason: 'it could not be moved' })
  }
  return out
}

let enforcing = false
/**
 * The standing rule: every EQ string in the model that has drifted gets equalized.
 * A couple of passes so strings sharing an element settle; strings that fight each
 * other simply stop after the last pass (the rule never loops).
 */
export function enforceEqDimensions(model: ModelDoc): number {
  if (enforcing) return 0
  // runs on EVERY local edit (drag frames included) — a cheap raw scan first, so a
  // model with no EQ strings never pays for decoding its drafting records
  let any = false
  for (const m of maps(model).drafting.values()) { if (m.get('eq') === true) { any = true; break } }
  if (!any) return 0
  const recs = listDrafting(model).filter((r) => r.eq === true && canEqDimension(r, model))
  if (!recs.length) return 0
  enforcing = true
  let n = 0
  try {
    for (let pass = 0; pass < 3; pass++) {
      let movedThisPass = 0
      for (const rec of recs) {
        if (isDimensionEqual(model, rec)) continue
        movedThisPass += equalizeDimension(model, rec).moved.length
      }
      n += movedThisPass
      if (!movedThisPass) break
    }
  } finally {
    enforcing = false
  }
  return n
}

registerPostTransactHook(enforceEqDimensions)
